// src/components/AIAnalysisHelper.js
import React, { useState, useEffect } from 'react';

export const AIAnalysisContext = React.createContext({
    isConnected: false,
    analyzeEvidence: async () => {}
});

export const AIAnalysisProvider = ({ children }) => {
    const [ws, setWs] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [responseHandlers] = useState(new Map());

    useEffect(() => {
        // Only attempt WebSocket connection in development mode when the bridge service is expected to be running
        const isDevelopment = process.env.NODE_ENV === 'development';
        const bridgeEnabled = process.env.REACT_APP_AI_BRIDGE_ENABLED === 'true';
        
        console.log('🔧 AI Bridge: Environment check:', {
            NODE_ENV: process.env.NODE_ENV,
            isDevelopment,
            REACT_APP_AI_BRIDGE_ENABLED: process.env.REACT_APP_AI_BRIDGE_ENABLED,
            bridgeEnabled,
            shouldConnect: isDevelopment && bridgeEnabled
        });
        
        if (!isDevelopment || !bridgeEnabled) {
            console.log('🔧 AI Bridge: Skipping WebSocket connection (not in development mode or bridge disabled)');
            setIsConnected(false);
            return;
        }

        console.log('🔧 AI Bridge: Setting up WebSocket connection...');
        const server = new WebSocket('ws://localhost:3001');
        let reconnectAttempts = 0;
        const maxReconnectAttempts = 3;
        
        server.onopen = () => {
            console.log('✅ AI Bridge connection successful');
            setIsConnected(true);
            reconnectAttempts = 0;
        };

        server.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('Received message from bridge:', data);

                if (data.type === 'analysis_result' && data.controlId) {
                    const handler = responseHandlers.get(data.controlId);
                    if (handler) {
                        // Handle the raw analysis directly
                        handler({ 
                            rawAnalysis: data.rawAnalysis,
                            success: true
                        });
                        responseHandlers.delete(data.controlId);
                    }
                } else if (data.type === 'error') {
                    console.error('Bridge error:', data.message);
                    const handler = responseHandlers.get(data.controlId);
                    if (handler) {
                        handler({ 
                            success: false, 
                            error: data.message 
                        });
                        responseHandlers.delete(data.controlId);
                    }
                }
            } catch (error) {
                console.error('Error processing bridge message:', error);
            }
        };

        server.onerror = (error) => {
            // Only log the first few errors to avoid spam and then stop
            if (reconnectAttempts < 3) {
                console.warn('⚠️ AI Bridge WebSocket connection failed (attempt ' + (reconnectAttempts + 1) + ')');
            }
            setIsConnected(false);
            
            // Prevent reconnection attempts if this keeps failing
            if (reconnectAttempts >= maxReconnectAttempts) {
                console.log('🔧 AI Bridge: Max reconnection attempts reached, stopping');
                return;
            }
        };

        server.onclose = (event) => {
            setIsConnected(false);
            reconnectAttempts++;
            
            // Only log meaningful close events, not failed connection attempts
            if (event.wasClean) {
                console.log('🔌 AI Bridge connection closed cleanly');
            } else if (reconnectAttempts <= maxReconnectAttempts) {
                console.warn(`⚠️ AI Bridge connection lost (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
            }
        };

        setWs(server);

        return () => {
            console.log('Cleaning up WebSocket connection...');
            if (server) server.close();
        };
    }, [responseHandlers]);

const analyzeEvidence = async (context) => {
    return new Promise((resolve) => {
        if (!ws || !isConnected) {
            resolve({ success: false, error: 'Bridge not connected' });
            return;
        }

        try {
            // Log the incoming context
            console.log('Analyzing with context:', context);

            // Register response handler with the controlId from context
            responseHandlers.set(context.controlId, resolve);

            // Create the message object before sending
            const message = {
                type: 'analyze_evidence',
                controlId: context.controlId,
                evidence: context.evidence,
                maturityLevel: context.maturityLevel,
                controlRequirement: context.controlRequirement,
                methodology: context.methodology,
                maturityContext: context.maturityContext
            };

            // Log the outgoing message
            console.log('Sending to bridge:', message);

            // Send message
            ws.send(JSON.stringify(message));
        } catch (error) {
            console.error('Error in analyzeEvidence:', error);
            resolve({ success: false, error: error.message });
        }
    });
};

    return (
        <AIAnalysisContext.Provider value={{ isConnected, analyzeEvidence }}>
            {children}
        </AIAnalysisContext.Provider>
    );
};

export const useAIAnalysis = () => {
    const context = React.useContext(AIAnalysisContext);
    if (!context) {
        throw new Error('useAIAnalysis must be used within an AIAnalysisProvider');
    }
    return context;
};